import { type ReactNode, useMemo } from "react";
import { Pressable, Text, View } from "react-native";
import {
  ArrowRight,
  ClipboardCheck,
  FlaskConical,
  MapPin,
  ShieldCheck,
  Star,
} from "lucide-react-native";
import { cardSurface } from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import { getJourneyStepCompletionId } from "../completion";
import { OrganicExplanatoryIllustration } from "./EggFreezingBiologyDeepCarouselCard";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import {
  TimelineActionRow,
  TimelineSubtaskCard,
} from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";
import {
  ClinicShortlistSection,
  type ClinicShortlistCriterion,
} from "./ClinicShortlistSection";
import { JourneyHandoffCard } from "./JourneyHandoffCard";
import { hasConditions } from "@src/packages/patients/utils/hasConditions";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const silkBrown =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;

type Props = {
  onAction: (action: PlanAction) => void;
};

type ClinicTimelineVariant =
  | "egg-freezing"
  | "embryo-freezing"
  | "sperm-freezing"
  | "ivf"
  | "iui"
  | "amab-family-building";

function buildClinicCopy(
  t: TranslationFunction,
  variant: ClinicTimelineVariant,
): {
  stepLabel: string;
  searchSubtitle: string;
  matchesTitle: string;
  matchesSubtitle: string;
  lockedDescription: string;
  emptyDescription: string;
  iconKind: "cryo" | "find-care" | "clinics";
  matchesIconKind: "cryo" | "clinics";
} {
  switch (variant) {
    case "egg-freezing":
      return {
        stepLabel: t("homepage.review_concept.clinic_step_label_egg"),
        searchSubtitle: t(
          "homepage.review_concept.clinic_search_subtitle_egg_freezing",
        ),
        matchesTitle: t("homepage.review_concept.clinic_matches_title_clinics"),
        matchesSubtitle: t(
          "homepage.review_concept.clinic_matches_subtitle_egg_freezing",
        ),
        lockedDescription: t(
          "homepage.review_concept.clinic_locked_description_egg_freezing",
        ),
        emptyDescription: t(
          "homepage.review_concept.clinic_empty_description_default",
        ),
        iconKind: "find-care",
        matchesIconKind: "clinics",
      };
    case "embryo-freezing":
      return {
        stepLabel: t("homepage.review_concept.clinic_step_label_find_care"),
        searchSubtitle: t(
          "homepage.review_concept.clinic_search_subtitle_embryo_freezing",
        ),
        matchesTitle: t("homepage.review_concept.clinic_matches_title_clinics"),
        matchesSubtitle: t(
          "homepage.review_concept.clinic_matches_subtitle_embryo_freezing",
        ),
        lockedDescription: t(
          "homepage.review_concept.clinic_locked_description_embryo_freezing",
        ),
        emptyDescription: t(
          "homepage.review_concept.clinic_empty_description_default",
        ),
        iconKind: "find-care",
        matchesIconKind: "clinics",
      };
    case "sperm-freezing":
      return {
        stepLabel: t("homepage.review_concept.clinic_step_label_find_care"),
        searchSubtitle: t(
          "homepage.review_concept.clinic_search_subtitle_sperm_freezing",
        ),
        matchesTitle: t("homepage.review_concept.clinic_matches_title_labs"),
        matchesSubtitle: t(
          "homepage.review_concept.clinic_matches_subtitle_sperm_freezing",
        ),
        lockedDescription: t(
          "homepage.review_concept.clinic_locked_description_sperm_freezing",
        ),
        emptyDescription: t(
          "homepage.review_concept.clinic_empty_description_labs",
        ),
        iconKind: "cryo",
        matchesIconKind: "cryo",
      };
    case "ivf":
      return {
        stepLabel: t("homepage.review_concept.clinic_step_label_find_care"),
        searchSubtitle: t("homepage.review_concept.clinic_search_subtitle_ivf"),
        matchesTitle: t("homepage.review_concept.clinic_matches_title_ivf"),
        matchesSubtitle: t(
          "homepage.review_concept.clinic_matches_subtitle_ivf",
        ),
        lockedDescription: t(
          "homepage.review_concept.clinic_locked_description_ivf",
        ),
        emptyDescription: t(
          "homepage.review_concept.clinic_empty_description_ivf",
        ),
        iconKind: "find-care",
        matchesIconKind: "clinics",
      };
    case "iui":
      return {
        stepLabel: t("homepage.review_concept.clinic_step_label_find_care"),
        searchSubtitle: t("homepage.review_concept.clinic_search_subtitle_iui"),
        matchesTitle: t("homepage.review_concept.clinic_matches_title_iui"),
        matchesSubtitle: t(
          "homepage.review_concept.clinic_matches_subtitle_iui",
        ),
        lockedDescription: t(
          "homepage.review_concept.clinic_locked_description_iui",
        ),
        emptyDescription: t(
          "homepage.review_concept.clinic_empty_description_iui",
        ),
        iconKind: "find-care",
        matchesIconKind: "clinics",
      };
    case "amab-family-building":
      return {
        stepLabel: t("homepage.review_concept.clinic_step_label_find_care"),
        searchSubtitle: t(
          "homepage.review_concept.clinic_search_subtitle_amab",
        ),
        matchesTitle: t("homepage.review_concept.clinic_matches_title_amab"),
        matchesSubtitle: t(
          "homepage.review_concept.clinic_matches_subtitle_amab",
        ),
        lockedDescription: t(
          "homepage.review_concept.clinic_locked_description_amab",
        ),
        emptyDescription: t(
          "homepage.review_concept.clinic_empty_description_amab",
        ),
        iconKind: "find-care",
        matchesIconKind: "clinics",
      };
  }
}

function getMatchingAction(
  criteria: ClinicShortlistCriterion[],
  variant: ClinicTimelineVariant,
  t: TranslationFunction,
): PlanAction {
  const missing = criteria.find((criterion) => !criterion.done);
  if (!missing) {
    return {
      label: t("homepage.review_concept.clinic_action_see_matches"),
      screen: "FindCareScreen",
      params: { filter: variant, mode: "matched" },
    };
  }

  if (missing.id === "history") {
    return {
      label: t("homepage.review_concept.clinic_action_complete_history"),
      screen: "MedicalHistory",
    };
  }

  if (missing.id === "insurance") {
    return {
      label: t("homepage.review_concept.clinic_action_check_coverage"),
      signal: "open-coverage-status-modal",
    };
  }

  return {
    label: t("homepage.review_concept.clinic_action_add_location"),
    screen: "FindCareScreen",
    params: { mode: "location" },
  };
}

export function ConciergeMatchingProgram({ onAction }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ConciergeMatchingProgram");
  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(t("homepage.review_concept.concierge_explore_label")),
          "homepage.review_concept.concierge_explore_label",
        );
        onAction({
          label: t("homepage.review_concept.concierge_explore_label"),
          screen: "FindCareScreen",
          params: { mode: "concierge" },
        });
      }}
      style={({ pressed }) => ({
        ...cardSurface(),
        padding: 15,
        opacity: pressed ? 0.86 : 1,
      })}
    >
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 11 }}>
        <View
          style={{
            width: 38,
            height: 38,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <OrganicMiniIcon kind="concierge" size={36} />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              lineHeight: 20,
              color: EMBER.colors.text,
              fontWeight: "700",
            }}
          >
            {t("homepage.review_concept.concierge_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              fontWeight: "400",
              marginTop: 4,
            }}
          >
            {t("homepage.review_concept.concierge_body")}
          </Text>
        </View>
        <ArrowRight
          size={16}
          color={EMBER.colors.textLight}
          strokeWidth={1.8}
        />
      </View>

      <View style={{ marginLeft: 49, marginTop: 10 }}>
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {[
            {
              label: t("homepage.review_concept.concierge_tag_testing"),
              icon: FlaskConical,
            },
            {
              label: t("homepage.review_concept.concierge_tag_doctors"),
              icon: Star,
            },
            {
              label: t("homepage.review_concept.concierge_tag_savings"),
              icon: ShieldCheck,
            },
          ].map(({ label, icon: Icon }) => (
            <View
              key={label}
              style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
            >
              <Icon
                size={12}
                color={EMBER.colors.textLight}
                strokeWidth={1.8}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  lineHeight: 15,
                  color: EMBER.colors.textLight,
                  fontWeight: "700",
                }}
              >
                {label}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </Pressable>
  );
}

export function EggFreezingClinicTimelineStep({
  onAction,
  variant = "egg-freezing",
  children,
}: Props & { variant?: ClinicTimelineVariant; children?: ReactNode }) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const { hasInsurance } = useCoverage();
  const copy = buildClinicCopy(t, variant);
  const criteria = useMemo<ClinicShortlistCriterion[]>(() => {
    const hasMedicalHistory = !!patient?.dateOfBirth || hasConditions(patient);
    const hasLocation = (patient?.addresses?.length ?? 0) > 0;
    return [
      {
        id: "history",
        label: t("homepage.review_concept.clinic_criteria_history_label"),
        doneLabel: t("homepage.review_concept.clinic_criteria_history_done"),
        missingLabel: t(
          "homepage.review_concept.clinic_criteria_history_missing",
        ),
        done: hasMedicalHistory,
        icon: ClipboardCheck,
      },
      {
        id: "location",
        label: t("homepage.review_concept.clinic_criteria_location_label"),
        doneLabel: t("homepage.review_concept.clinic_criteria_location_done"),
        missingLabel: t(
          "homepage.review_concept.clinic_criteria_location_missing",
        ),
        done: hasLocation,
        icon: MapPin,
      },
      {
        id: "insurance",
        label: t("homepage.review_concept.clinic_criteria_coverage_label"),
        doneLabel: t("homepage.review_concept.clinic_criteria_coverage_done"),
        missingLabel: t(
          "homepage.review_concept.clinic_criteria_coverage_missing",
        ),
        done: hasInsurance,
        icon: ShieldCheck,
      },
    ];
  }, [hasInsurance, patient, t]);

  const findCareLabel = t("homepage.review_concept.clinic_action_find_care");

  return (
    <StepperSection
      id={`precycle-${variant}.clinic.timeline`}
      stepLabel={copy.stepLabel}
      completionStepId={
        variant === "egg-freezing"
          ? getJourneyStepCompletionId("egg-freezing-solo", "clinic")
          : "find-care"
      }
      showRail={false}
    >
      <TimelineActionRow
        title={findCareLabel}
        subtitle={copy.searchSubtitle}
        icon={<OrganicMiniIcon kind={copy.iconKind} size={34} />}
        onPress={() =>
          onAction({
            label: findCareLabel,
            screen: "FindCareScreen",
            params: { filter: variant },
          })
        }
      />

      <TimelineSubtaskCard
        id={`precycle-${variant}.clinic.timeline.matches`}
        title={copy.matchesTitle}
        subtitle={copy.matchesSubtitle}
        titleIcon={<OrganicMiniIcon kind={copy.matchesIconKind} size={34} />}
        collapsedPreviewHeight={68}
      >
        <ClinicShortlistSection
          criteria={criteria}
          lockedDescription={copy.lockedDescription}
          emptyDescription={copy.emptyDescription}
          resolveAction={(currentCriteria) =>
            getMatchingAction(currentCriteria, variant, t)
          }
          onAction={onAction}
        />
      </TimelineSubtaskCard>

      <ConciergeMatchingProgram onAction={onAction} />
      {children}
    </StepperSection>
  );
}

export function EggFreezingCycleStartTimelineStep({ onAction }: Props) {
  const t = useTranslations();
  return (
    <JourneyHandoffCard
      title={t("homepage.review_concept.cycle_start_title")}
      body={t("homepage.review_concept.cycle_start_body")}
      ctaLabel={t("homepage.review_concept.cycle_start_cta")}
      checklist={[
        t("homepage.review_concept.cycle_start_checklist_timing"),
        t("homepage.review_concept.cycle_start_checklist_medication"),
        t("homepage.review_concept.cycle_start_checklist_monitoring"),
        t("homepage.review_concept.cycle_start_checklist_daily_guidance"),
      ]}
      backgroundImage={silkBrown}
      illustration={<OrganicExplanatoryIllustration kind="follicle-cohort" />}
      onPress={() =>
        onAction({
          label: t("homepage.review_concept.cycle_start_action_label"),
          signal: "open-start-treatment-modal",
          params: { presetCycleType: "egg_freezing" },
        })
      }
    />
  );
}

export function EggFreezingPrepTimelineRemainingSteps({ onAction }: Props) {
  return (
    <>
      <EggFreezingClinicTimelineStep onAction={onAction} />
    </>
  );
}

export function EggFreezingPrepTimelineTransitionCards({ onAction }: Props) {
  return (
    <>
      <EggFreezingCycleStartTimelineStep onAction={onAction} />
    </>
  );
}
